import moment = require('moment');
import { FormGroup } from '@angular/forms';
import {
  IIbanObject,
  IOperationPayment,
} from '../modules/athora/athora.constants';
import {
  EBeneficiaryType,
  ERelationshipWithSubscriberDesc,
  ESpecificBeneficiaryType,
} from '../modules/athora/components/beneficiary-form/beneficiary-form.constants';
import { beneficiary_fields } from '../modules/athora/components/claim-notification/claim-notification.constants';
import {
  CODICE_NAZIONE_ITALIA,
  ISubjectPersonaFisica,
  ISubjectPersonaGiuridica,
} from '../modules/athora/components/contractor-variation/contractor-variation.constants';
import { IAnagrafica } from '../modules/shared/components/ndg-variation/ndg-variation.constants';
import { IFormKey } from '../modules/shared/models/forms';
import { IQuestionnaireAnswers } from '../modules/shared/models/operation.model';
import {
  DATE_FORMAT_FROM_BE,
  ESubjectType,
  IBeneficiary,
  IBeneficiaryObject,
} from '../modules/shared/models/policy.model';
import { LoggerService } from '../services/logger.service';
import { CommonUtils } from './CommonUtils';

export class AthoraPayloadUtils {
  static getPaymentObject(
    accountHolder: string,
    ibanObj: IIbanObject
  ): IOperationPayment {
    if (ibanObj) {
      let payment: IOperationPayment = null;
      const codicePaese = CommonUtils.checkAndGet(ibanObj, 'codicePaese', null);
      if (codicePaese === 'IT') {
        payment = {
          paymentMethod: 'Bonifico',
          iban: this.formatIBANString(ibanObj),
          accountHolder: accountHolder,
        };
      } else {
        payment = {
          paymentMethod: 'Bonifico Estero',
          foreignAccount: this.formatIBANString(ibanObj),
          bic: CommonUtils.checkAndGet(ibanObj, 'codiceBIC', null),
          foreignCurrency: null,
          uic: null,
          bankAddress: null,
        };
      }
      return payment;
    }
    return null;
  }

  static formatIBANString(ibanObj: IIbanObject): string {
    if (ibanObj) {
      const codicePaese: string = CommonUtils.checkAndGet(
        ibanObj,
        'codicePaese',
        null
      );
      const codEur: string = CommonUtils.checkAndGet(
        ibanObj,
        'codiceInternazionaleEuropeo',
        null
      );
      const codNazionale: string = CommonUtils.checkAndGet(
        ibanObj,
        'codiceNazionaleBBAN',
        null
      );
      const abi: string = CommonUtils.checkAndGet(ibanObj, 'abiRapporto', null);
      const cab: string = CommonUtils.checkAndGet(ibanObj, 'cabRapporto', null);
      const numeroConto: string = CommonUtils.checkAndGet(
        ibanObj,
        'numeroRapporto',
        null
      );
      return `${codicePaese}${codEur}${codNazionale}${abi}${cab}${numeroConto}`;
    }
    return null;
  }

  static formatCCString(ibanObj: IIbanObject): string {
    if (ibanObj) {
      const abi: string = CommonUtils.checkAndGet(ibanObj, 'abiRapporto', null);
      const cab: string = CommonUtils.checkAndGet(ibanObj, 'cabRapporto', null);
      const numeroConto: string = CommonUtils.checkAndGet(
        ibanObj,
        'numeroRapporto',
        null
      );
      return `${abi}.${cab}.${numeroConto}`;
    }
  }

  static formatQuestionnaireAnswers(
    questionnaireCode: string,
    questionnaireFormKeys: IFormKey[],
    questionnaireForm: FormGroup,
    fiscalCode: string
  ): IQuestionnaireAnswers {
    const formattedQuestionnaire: IQuestionnaireAnswers = {
      fiscalCode: fiscalCode,
      questionnaireCode: questionnaireCode,
      answers: {},
    };
    const rawForm = questionnaireForm.getRawValue();
    const list = Object.entries(rawForm);
    list.forEach((item) => {
      const questionnaireFormKey = questionnaireFormKeys?.find(
        (x) => x.controlName === item[0]
      );
      const hidden = questionnaireFormKey?.hidden;
      const isInternal = questionnaireFormKey?.isInternal;
      if (item[1] && !hidden && !isInternal) {
        let value = item[1] as string;
        if (value.length > 1) {
          value = value.split(')')[0];
        }
        formattedQuestionnaire.answers[item[0]] = value;
      }
    });
    LoggerService.logInfo(this.constructor.name, 'getQuestionnairesObj', {
      rawForm,
    });
    return formattedQuestionnaire;
  }

  static formatExistingBeneficiaryObject(
    subject: IAnagrafica,
    fieldsArray: string[],
    additionalData: any = null
  ): IBeneficiaryObject {
    const isLegalSubject = subject?.['ns2:naturaGiuridica'] !== ESubjectType.PF;
    const descBeneficiary = isLegalSubject
      ? subject?.['ns2:partitaIva'] +
        ' - ' +
        `${subject?.['ns2:ragioneSocialeRiga1']}${subject?.['ns2:ragioneSocialeRiga2']}`
      : subject?.['ns2:codiceFiscale'] +
        ' - ' +
        subject?.['ns2:nome'] +
        ' ' +
        subject?.['ns2:cognome'];
    let newBeneficiary: IBeneficiaryObject = {
      codeChoice: 'Altro beneficiario',
      descBeneficiary: descBeneficiary,
      type: 'Morte',
      quote: additionalData?.quote,
      toBeDeleted: false,
      isNewBeneficiary: true,
      relationshipWithSubscriber:
        ERelationshipWithSubscriberDesc[
          additionalData?.relationshipWithSubscriber
        ],
    };
    newBeneficiary['isLegalSubject'] = isLegalSubject ? 'S' : 'N';
    const splittedAddress = subject?.['ns2:indirizzoSedeLegale'].split(' ');
    const keys: string[] = Object.keys(beneficiary_fields);
    fieldsArray.forEach((field) => {
      if (keys.includes(field)) {
        newBeneficiary[field] = subject[beneficiary_fields[field]];
      }
    });
    newBeneficiary['residenceCountry'] =
      subject?.['ns2:codiceNazioneSedeLegale'] !== ''
        ? subject?.['ns2:codiceNazioneSedeLegale']
        : CODICE_NAZIONE_ITALIA;
    newBeneficiary['residenceToponym'] = splittedAddress[0];
    newBeneficiary['residenceAddress'] = splittedAddress.slice(1, -1).join(' ');
    newBeneficiary['residenceAddressNumber'] =
      splittedAddress[splittedAddress.length - 1];
    if (!isLegalSubject) {
      newBeneficiary['birthPlace'] = subject?.['ns2:comuneNascita'];
      newBeneficiary['birthProvince'] = subject?.['ns2:provinciaNascita'];
      newBeneficiary['birthCountry'] =
        subject?.['ns2:codiceNazioneNascita'] !== ''
          ? subject?.['ns2:codiceNazioneNascita']
          : CODICE_NAZIONE_ITALIA;
    } else {
      newBeneficiary['companyName'] =
        subject?.['ns2:ragioneSocialeRiga1'] +
        subject?.['ns2:ragioneSocialeRiga2'];
    }
    newBeneficiary['birthDate'] = moment(newBeneficiary['birthDate']).format(
      DATE_FORMAT_FROM_BE
    );
    newBeneficiary['iban'] = additionalData?.iban;
    LoggerService.logInfo(
      this.constructor.name,
      'formatExistingBeneficiaryObject',
      newBeneficiary
    );
    return newBeneficiary;
  }

  static formatNewBeneficiaryObject(
    outcome: any,
    fieldsArray: string[]
  ): IBeneficiaryObject {
    const isGenericBeneficiary =
      outcome?.beneficiaryType === EBeneficiaryType.GENERIC_BENEFICIARY;
    const isLegalSubject =
      outcome?.specificBeneficiaryType ===
      ESpecificBeneficiaryType.PERSONA_GIURIDICA;
    const descBeneficiary = isGenericBeneficiary
      ? outcome?.genericBeneficiary
      : isLegalSubject
      ? outcome?.vatCode + ' - ' + outcome?.companyName
      : outcome?.taxCode + ' - ' + outcome?.name + ' ' + outcome?.surname;
    const newBeneficiary: IBeneficiaryObject = {
      codeChoice: isGenericBeneficiary
        ? 'Beneficiario Testo Libero'
        : 'Altro beneficiario',
      type: outcome?.beneficiaryTypeDesc,
      descBeneficiary: descBeneficiary,
      quote: isGenericBeneficiary ? 100 : outcome?.quote,
      isNewBeneficiary: true,
      toBeDeleted: false,
    };
    if (!isGenericBeneficiary) {
      newBeneficiary['isLegalSubject'] = isLegalSubject ? 'S' : 'N';
      fieldsArray.forEach((field) => {
        if (!newBeneficiary[field]) {
          if (field === 'relationshipWithSubscriber') {
            newBeneficiary[field] =
              ERelationshipWithSubscriberDesc[outcome[field]];
          } else if (field === 'birthDate') {
            newBeneficiary[field] = moment(outcome[field]).format(
              DATE_FORMAT_FROM_BE
            );
          } else {
            newBeneficiary[field] = outcome[field];
          }
        }
      });
    }
    LoggerService.logInfo(
      this.constructor.name,
      'formatNewBeneficiaryObject',
      newBeneficiary
    );
    return newBeneficiary;
  }

  static formatSubjectFromBeneficiaryObject(
    beneficiary: IBeneficiaryObject
  ): ISubjectPersonaFisica | ISubjectPersonaGiuridica {
    if (
      !beneficiary.toBeDeleted &&
      beneficiary.codeChoice !== 'Beneficiario Testo Libero'
    ) {
      let formattedSubject;
      if (beneficiary.isLegalSubject === 'N') {
        formattedSubject = this.mapPhysicalSubject(beneficiary);
      } else {
        formattedSubject = {
          birthDate: beneficiary.birthDate,
          residence: {
            country: beneficiary.residenceCountry,
            place: beneficiary.residencePlace,
            province: beneficiary.residenceProvince,
            postalCode: beneficiary.residencePostalCode,
            toponym: beneficiary.residenceToponym,
            address: beneficiary.residenceAddress,
            addressNumber: beneficiary.residenceAddressNumber,
          },
          companyName: beneficiary.companyName,
          isLegalSubject: 'S',
          vatNumber: beneficiary.vatCode,
          shareCapital: beneficiary.shareCapital,
        };
      }
      formattedSubject['privacy'] = this.setSubjectPrivacy();
      return formattedSubject;
    }
    return null;
  }

  static mapPhysicalSubject(data: any) {
    return {
      citizenship: data.citizenship,
      fiscalCode: data.taxCode,
      isLegalSubject: 'N',
      name: data.name,
      surname: data.surname,
      birthDate: moment(data.birthDate).format(DATE_FORMAT_FROM_BE),
      sex: data.sex,
      occupation: '',
      birthAddress: {
        place: data.birthPlace,
        country: data.birthCountry,
        province: data.birthProvince,
      },
      residence: {
        country: data.residenceCountry,
        place: data.residencePlace,
        province: data.residenceProvince,
        postalCode: data.residencePostalCode,
        toponym: data.residenceToponym,
        address: data.residenceAddress,
        addressNumber: data.residenceAddressNumber,
      },
    };
  }

  static setSubjectPrivacy(): any {
    return {
      '5) ATHORA: Contrattuale': 'S',
      '6) ATHORA: Commerciale': 'N',
      '7) ATHORA: Categorie particolari di dati personali': 'N',
      '8) ATHORA: Profilazione e Customer Satisfaction': 'N',
    };
  }

  static formatBeneficariesFromBeneficiariesObject(
    beneficiaries: IBeneficiaryObject[]
  ): IBeneficiary[] {
    const genericBeneficiaries: IBeneficiary = {
      codeChoice: 'Beneficiario Testo Libero',
      type: 'Morte',
      roles: [],
    };
    const specificBeneficiaries: IBeneficiary = {
      codeChoice: 'Altro beneficiario',
      type: 'Morte',
      roles: [],
    };
    if (beneficiaries) {
      beneficiaries.forEach((beneficiary: IBeneficiaryObject) => {
        if (beneficiary.codeChoice === 'Beneficiario Testo Libero') {
          genericBeneficiaries.descBeneficiary = beneficiary.descBeneficiary;
          genericBeneficiaries.roles.push({
            toBeDeleted: beneficiary.toBeDeleted ? 'S' : 'N',
            role: 'Beneficiario',
            percentage: beneficiary.quote?.replace(',', '.'),
          });
        } else {
          specificBeneficiaries.roles.push({
            toBeDeleted: beneficiary.toBeDeleted ? 'S' : 'N',
            role: 'Beneficiario',
            fiscalCode: beneficiary.taxCode ?? beneficiary.vatCode,
            percentage: beneficiary.quote?.replace(',', '.'),
            relationshipWithSubscriber: beneficiary.relationshipWithSubscriber,
          });
        }
      });
    }
    if (genericBeneficiaries.roles.length > 0) {
      genericBeneficiaries.roles = genericBeneficiaries.roles.filter(
        (role) => role.toBeDeleted === 'N'
      );
    }
    return [
      genericBeneficiaries?.roles?.length > 0 ? genericBeneficiaries : null,
      specificBeneficiaries?.roles?.length > 0 ? specificBeneficiaries : null,
    ].filter((item) => item !== null);
  }

  static formatSubjectFromDettaglioBPER(
    subject: IAnagrafica,
    shareCapital: string = null
  ): ISubjectPersonaFisica | ISubjectPersonaGiuridica {
    let formattedSubject: ISubjectPersonaFisica | ISubjectPersonaGiuridica;
    const isSearchedSubjectPF: boolean =
      subject?.['ns2:naturaGiuridica'] === ESubjectType.PF ? true : false;
    const splittedAddress = subject?.['ns2:indirizzoSedeLegale'].split(' ');
    if (isSearchedSubjectPF) {
      const docAuthority =
        subject?.['ns2:datiDocumento']?.['ns2:autoritaEmissione'];
      formattedSubject = {
        citizenship: subject?.['ns2:codicePrimaCittadinanza'],
        fiscalCode: subject?.['ns2:codiceFiscale'],
        isLegalSubject: 'N',
        name: subject?.['ns2:nome'],
        sex: subject?.['ns2:sesso'],
        surname: subject?.['ns2:cognome'],
        birthDate: moment(subject?.['ns2:dataNascita']).format(
          DATE_FORMAT_FROM_BE
        ),
        birthAddress: {
          country:
            subject?.['ns2:codiceNazioneNascita'] !== ''
              ? subject?.['ns2:codiceNazioneNascita']
              : CODICE_NAZIONE_ITALIA,
          place: subject?.['ns2:comuneNascita'],
          province: subject?.['ns2:provinciaNascita'],
        },
        residence: {
          country:
            subject?.['ns2:codiceNazioneSedeLegale'] !== ''
              ? subject?.['ns2:codiceNazioneSedeLegale']
              : CODICE_NAZIONE_ITALIA,
          place: subject?.['ns2:comuneSedeLegale'],
          province: subject?.['ns2:provinciaSedeLegale'],
          postalCode: subject?.['ns2:capSedeLegale'],
          toponym: splittedAddress[0],
          address: splittedAddress.slice(1, -1).join(' '),
          addressNumber: splittedAddress[splittedAddress.length - 1],
        },
        occupation: '',
        docType: subject?.['ns2:datiDocumento']['ns2:tipo'],
        docNumber: subject?.['ns2:datiDocumento']['ns2:numero'],
        docReleaseDate: moment(
          subject?.['ns2:datiDocumento']['ns2:dataEmissione']
        ).format(DATE_FORMAT_FROM_BE),
        docExpiryDate: moment(
          subject?.['ns2:datiDocumento']['ns2:dataScadenza']
        ).format(DATE_FORMAT_FROM_BE),
        docPlace: subject?.['ns2:datiDocumento']['ns2:luogoEmissione'],
        docAuthority:
          docAuthority?.[0].toUpperCase() +
          docAuthority?.slice(1)?.toLowerCase(),
        privacy: this.setSubjectPrivacy(),
      };
    } else {
      formattedSubject = {
        residence: {
          country:
            subject?.['ns2:codiceNazioneSedeLegale'] !== ''
              ? subject?.['ns2:codiceNazioneSedeLegale']
              : CODICE_NAZIONE_ITALIA,
          place: subject?.['ns2:comuneSedeLegale'],
          province: subject?.['ns2:provinciaSedeLegale'],
          postalCode: subject?.['ns2:capSedeLegale'],
          toponym: splittedAddress[0],
          address: splittedAddress.slice(1, -1).join(' '),
          addressNumber: splittedAddress[splittedAddress.length - 1],
        },
        birthDate: moment(subject?.['ns2:dataNascita']).format(
          DATE_FORMAT_FROM_BE
        ),
        companyName:
          subject?.['ns2:ragioneSocialeRiga1'] +
          subject?.['ns2:ragioneSocialeRiga2'],
        isLegalSubject: 'S',
        vatNumber: subject?.['ns2:codiceFiscale'],
        shareCapital: shareCapital,
      };
    }
    return formattedSubject;
  }
}
